/**
 * useMobileAnalytics — Story 1.3 (Mobile Dashboard & Carrier-Breakdown)
 *
 * Duenne Hooks auf die in Welle 1 gelieferten Mobile-Analytics-Endpunkte
 * (generierte api-types, Story 1.2):
 *  - useMobileSummary:          GET /api/v2/mobile/analytics/summary
 *  - useMobileCarrierBreakdown: GET /api/v2/mobile/analytics/carrier_breakdown
 *
 * Beide Endpunkte sind account-weit (optionaler hotspot_ids-Filter, KEIN
 * Tag-Filter im Contract) — die Dashboard-Sektion wird deshalb im Tag-Scope
 * ausgeblendet statt falsch gescoped zu rendern.
 */
import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'
import type { operations } from '@heliumtracker/api-types'

export type MobilePeriod = '24h' | '7d' | '30d' | '90d'
export type CarrierGranularity = 'hour' | 'day' | 'month'

export type MobileSummary =
  operations['getApiV2MobileAnalyticsSummary']['responses'][200]['content']['application/json']['data']

export type MobileCarrierBreakdown =
  operations['getApiV2MobileAnalyticsCarrierBreakdown']['responses'][200]['content']['application/json']['data']

type HookResult<T> = {
  data: T | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function useMobileSummary(period: MobilePeriod): HookResult<MobileSummary> {
  const query = useApi<MobileSummary>(
    ['dashboard', 'mobile-analytics', 'summary', period],
    async () => {
      const result = await apiClient.GET('/api/v2/mobile/analytics/summary', {
        params: { query: { period } },
      })
      if (result.error) return { error: result.error }
      return { data: result.data?.data }
    },
  )
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}

export function useMobileCarrierBreakdown(
  period: MobilePeriod,
  granularity: CarrierGranularity,
): HookResult<MobileCarrierBreakdown> {
  const query = useApi<MobileCarrierBreakdown>(
    ['dashboard', 'mobile-analytics', 'carrier-breakdown', period, granularity],
    async () => {
      const result = await apiClient.GET('/api/v2/mobile/analytics/carrier_breakdown', {
        params: { query: { period, granularity } },
      })
      if (result.error) return { error: result.error }
      return { data: result.data?.data }
    },
  )
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}

/**
 * Trend innerhalb des Zeitraums: zweite Haelfte der Zeitreihe vs. erste
 * Haelfte, in Prozent. Der Contract liefert KEINEN Vorperioden-Vergleich
 * (siehe open_issue der Story) — dieser Trend ist bewusst als
 * Innerhalb-des-Zeitraums-Trend beschriftet und rein aus `series` abgeleitet,
 * nicht erfunden.
 *
 * REVIEW-FIX F3: Bei ungerader Bucket-Anzahl waren die Haelften vorher
 * unterschiedlich lang (mid = floor(n/2) -> erste Haelfte 15 Buckets, zweite
 * 16). Konstantes Volumen ergab damit einen erfundenen Trend von +6,7 %.
 * Jetzt werden zwei GLEICH LANGE Haelften verglichen (der mittlere Bucket
 * einer ungeraden Serie faellt heraus), und der Trend erscheint erst ab vier
 * Buckets — darunter ist „zweite vs. erste Haelfte" reines Rauschen.
 *
 * Rueckgabe null, wenn zu wenige Buckets (< 4) oder die erste Haelfte 0 ist
 * (Division nicht sinnvoll).
 */
export const MIN_BUCKETS_FOR_TREND = 4

export function seriesHalfTrendPct(series: MobileSummary['series'] | undefined): number | null {
  if (!series || series.length < MIN_BUCKETS_FOR_TREND) return null
  const half = Math.floor(series.length / 2)
  const sum = (slice: MobileSummary['series']) =>
    slice.reduce((acc, s) => acc + (Number.isFinite(s.gb) && s.gb > 0 ? s.gb : 0), 0)
  const first = sum(series.slice(0, half))
  const second = sum(series.slice(series.length - half))
  if (first <= 0) return null
  return ((second - first) / first) * 100
}
